/**
 * FieldRenderer Component
 *
 * Dispatcher component that renders the appropriate field preview
 * based on the field type.
 */

import React from 'react';
import { FieldRendererProps } from './types';
import TextFieldPreview from './TextFieldPreview';
import IntegerFieldPreview from './IntegerFieldPreview';
import DecimalFieldPreview from './DecimalFieldPreview';
import FractionFieldPreview from './FractionFieldPreview';
import BooleanFieldPreview from './BooleanFieldPreview';
import RangeFieldPreview from './RangeFieldPreview';
import MultiChoiceFieldPreview from './MultiChoiceFieldPreview';
import {
  TextFieldConfig,
  IntegerFieldConfig,
  DecimalFieldConfig,
  FractionFieldConfig,
  BooleanFieldConfig,
  RangeFieldConfig,
  MultiChoiceFieldConfig,
  Choice,
} from '../../config/fieldDefaults';
import { normalizeFieldType } from '../../utils/normalizeFieldType';
import { FieldChoiceOption } from '../../types/api.types';

/**
 * Transform API choice format (FieldChoiceOption) to preview format (Choice)
 * Maps: av_id → id, av_value → value, display_value → label
 */
const transformChoices = (apiChoices?: FieldChoiceOption[]): Choice[] => {
  if (!apiChoices || apiChoices.length === 0) return [];
  return apiChoices.map(choice => ({
    id: choice.av_id,
    value: choice.av_value,
    label: choice.display_value || choice.av_value,
    imageUrl: choice.product_image,
    disabled: choice.visibility === false,
  }));
};

export const FieldRenderer = ({
  fieldType,
  fieldName,
  config,
  dataConfiguration,
  additionalData,
  isSelected,
  onClick,
}: FieldRendererProps) => {
  const commonProps = { isSelected, onClick, fieldName };

  // Normalize fieldType to handle variations like "Multi-choice" → "multichoice"
  // API may return hyphenated or capitalized types (e.g., 'Multi-choice', 'Text', 'Integer')
  const normalizedFieldType = normalizeFieldType(fieldType);

  switch (normalizedFieldType) {
    case 'text':
      return (
        <TextFieldPreview
          config={config as unknown as TextFieldConfig}
          {...commonProps}
        />
      );
    case 'integer':
      return (
        <IntegerFieldPreview
          config={config as unknown as IntegerFieldConfig}
          {...commonProps}
        />
      );
    case 'decimal':
      return (
        <DecimalFieldPreview
          config={config as unknown as DecimalFieldConfig}
          {...commonProps}
        />
      );
    case 'fraction':
      return (
        <FractionFieldPreview
          config={config as unknown as FractionFieldConfig}
          {...commonProps}
        />
      );
    case 'boolean':
      return (
        <BooleanFieldPreview
          config={config as unknown as BooleanFieldConfig}
          {...commonProps}
        />
      );
    case 'range':
      return (
        <RangeFieldPreview
          config={config as unknown as RangeFieldConfig}
          {...commonProps}
        />
      );
    case 'multichoice': {
      // Transform choices from dataConfiguration (API format) to preview format
      const transformedChoices = transformChoices(dataConfiguration?.choices);
      // Merge: dataConfiguration.choices takes precedence, fallback to config.choices
      const multiChoiceConfig: MultiChoiceFieldConfig = {
        ...(config as unknown as MultiChoiceFieldConfig),
        choices: transformedChoices.length > 0
          ? transformedChoices
          : (config as unknown as MultiChoiceFieldConfig).choices || [],
      };
      return (
        <MultiChoiceFieldPreview
          config={multiChoiceConfig}
          additionalData={additionalData}
          {...commonProps}
        />
      );
    }
    default:
      return (
        <div role="button" data-testid="fields-fieldrenderer-div-1"
          className={`tw-p-4 tw-border tw-rounded tw-bg-surface-sunken ${
            isSelected ? 'tw-border-accent tw-ring-2 tw-ring-accent-tint' : 'tw-border-edge'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
        >
          <p className="tw-text-sm tw-text-ink-secondary">
            Unknown field type: {fieldType}
          </p>
        </div>
      );
  }
};

export default FieldRenderer;
